import { AsyncPipe, CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { padValue } from '@celerofinancas/core-ts';
import { TrackingService } from '@celerofinancas/service-utils';
import { getBankLogoUrl } from '@shared/utils/open-finance-bank-map';
import { OpenFinanceDirectoryService } from '@shared/services/open-finance-directory.service';
import { CompanyState } from '@celerofinancas/state-management';
import { NoContentModule } from '@celerofinancas/ui-common';
import { InfiniteScrollDirective, TooltipDirective } from '@celerofinancas/ui-directives';
import { FeatureFlagService } from '@celerofinancas/ui-feature-flag';
import { ModalRef, ModalService } from '@celerofinancas/ui-modals';
import { FormatCashPipe } from '@celerofinancas/ui-pipes';
import { DataTableComponent } from '@shared/components/data-table/data-table.component';
import {
  DataTableAction,
  DataTableColumn,
  DataTableConfig,
  DataTableRow,
  DataTableSort,
} from '@shared/components/data-table/data-table.interface';
import { MainTitleComponent } from '@shared/components/main-title/main-title.component';
import { NavigationBarComponent } from '@shared/components/navigation-bar/navigation-bar.component';
import { ShimmerComponent } from '@shared/components/shimmer/shimmer.component';
import {
  TabsNavigationComponent,
  TabsNavigationConfig,
} from '@shared/components/tabs-navigation/tabs-navigation.component';
import { BootstrapCoordinator } from '@shared/models/bootstrap-coordinator';
import { finalize, NEVER, take, takeUntil } from 'rxjs';
import { cashIndicatorClass, signedCash, withCashIndicator } from '@shared/utils/cash-indicator';
import {
  CreditLineDetailsResponse,
  CreditLinesComponentData,
  CreditLinesListApiResponse,
  CreditLineValue,
  InstitutionBalance,
  InstitutionValue,
  PaginationMeta,
} from './credit-lines.interfaces';
import { CreditLinesService } from './credit-lines.service';
import { CreditLineDetailsModalContentComponent } from './modal-content/modal-content.component';
import { CreditLineDetailsData } from './modal-content/modal-content.interface';
import { environment } from '@environments/environment';
import { CAIXA_TRACKING_MODELS } from '@shared/models/tracking-models.enum';

/**
 * Credit Lines Component
 */
@Component({
  selector: 'caixa-webcomponent-credit-lines',
  standalone: true,
  imports: [
    CommonModule,
    MainTitleComponent,
    NavigationBarComponent,
    TooltipDirective,
    InfiniteScrollDirective,
    DataTableComponent,
    TabsNavigationComponent,
    FormatCashPipe,
    ShimmerComponent,
    NoContentModule,
    AsyncPipe,
  ],
  templateUrl: './credit-lines.component.html',
  styleUrls: ['./credit-lines.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreditLinesComponent extends BootstrapCoordinator implements OnInit {
  /**
   * API page size
   */
  private static readonly PAGE_SIZE = 20;

  /**
   * Signs an amount by its natural direction, so the cash pipes render the
   * matching C/D indicator. Exposed for the template (card 86e37v532).
   */
  public readonly signedCash = signedCash;

  /**
   * Resolves the color class from the C/D indicator an amount renders.
   * Exposed for the template (card 86e37v532).
   */
  public readonly cashIndicatorClass = cashIndicatorClass;

  /**
   * Feature flag
   */
  public readonly featureFlag = inject(FeatureFlagService);

  /**
   * Currently active tab identifier
   */
  public activeTab = 'emprestimos';

  /**
   * Last update timestamp
   */
  public lastUpdate = new Date();

  /**
   * Loading flag for contracts table
   */
  public isLoadingContracts = signal(false);

  /**
   * Loading flag for summary cards
   */
  public isLoadingSummary = signal(false);

  /**
   * Loading flag for contract details modal
   */
  public loadingDetails = signal(false);

  /**
   * Loading flag while fetching next page
   */
  public isLoadingMoreContracts = signal(false);

  /**
   * Pagination state for contracts list
   */
  public pagination = signal<PaginationMeta>({
    page: 1,
    pageSize: CreditLinesComponent.PAGE_SIZE,
    totalItems: 0,
    totalPages: 1,
  });

  /**
   * Current sort configuration
   */
  public currentSort: { sortBy: string; sortOrder: 'asc' | 'desc' } | null = null;

  /**
   * Tabs navigation configuration
   */
  public tabsConfig: TabsNavigationConfig = {
    tabs: [
      { id: 'emprestimos', label: 'Empréstimos' },
      { id: 'financiamentos', label: 'Financiamentos' },
      { id: 'adiantamento', label: 'Adiantamento a depositantes' },
      { id: 'direitos-creditorios', label: 'Direitos creditórios descontados' },
    ],
    activeTab: 'emprestimos',
  };

  /**
   * Table columns configuration
   */
  public tableColumns: DataTableColumn[] = [
    {
      key: 'institutionName',
      label: 'Instituição',
      sortable: false,
      width: '80px',
      align: 'center',
      renderType: 'logo',
      renderConfig: {
        logoConfig: {
          imageUrlKey: 'institutionLogoUrl',
          colorMap: {
            'Banco Bradesco S.A.': '#1E40AF',
            'Banco do Brasil S.A.': '#059669',
            'Banco Safra S.A.': '#10B981',
            'Itaú Unibanco S.A.': '#F59E0B',
            'Banco Daycoval S.A.': '#3B82F6',
            'Banco Santander (Brasil) S.A.': '#DC2626',
          },
          textMap: {
            'Banco Bradesco S.A.': 'B',
            'Banco do Brasil S.A.': 'BB',
            'Banco Safra S.A.': 'S',
            'Itaú Unibanco S.A.': 'itaú',
            'Banco Daycoval S.A.': 'D',
            'Banco Santander (Brasil) S.A.': 'S',
          },
          defaultColor: 'var(--cds-text-light)',
        },
      },
    },
    {
      key: 'productName',
      label: 'Nome do contrato',
      sortable: true,
      width: '250px',
      align: 'left',
    },
    {
      key: 'contractNumber',
      label: 'Número do contrato',
      sortable: false,
      width: '200px',
      align: 'left',
    },
    {
      key: 'isRevolvingCredit',
      label: 'Crédito rotativo',
      sortable: true,
      width: '100px',
      align: 'center',
      tooltip:
        '<b>Crédito Rotativo:</b> linha de crédito com limite pré-aprovado que você pode usar, pagar e reutilizar conforme precisar, pagando juros apenas sobre o valor utilizado.<br><b>Crédito Não Rotativo:</b> linha de crédito com valor e prazo fixos, onde você recebe um montante definido e paga parcelas regulares até quitar a dívida.',
      renderType: 'status',
      renderConfig: {
        statusMap: {
          true: { text: 'SIM', class: 'status-default' },
          false: { text: 'NÃO', class: 'status-default' },
        },
      },
    },
    {
      key: 'contractAmount',
      label: 'Limite ou Valor contratado',
      sortable: true,
      width: '160px',
      align: 'right',
      tooltip:
        'Para contratos de crédito rotativo (como conta garantida), este valor corresponde ao limite disponível. Para contratos não rotativos (como capital de giro), representa o valor originalmente contratado no momento da operação.',
      renderType: 'currency',
      renderConfig: {
        currency: 'BRL',
        showSymbol: true,
        customClass: 'currency-dark',
      },
    },
    {
      key: 'outstandingBalance',
      label: 'Saldo devedor atual',
      sortable: true,
      width: '180px',
      align: 'right',
      renderType: 'currency',
      renderConfig: {
        currency: 'BRL',
        showSymbol: true,
        customClass: 'currency-expense',
      },
    },
    {
      key: 'paymentStatus',
      label: 'Status',
      sortable: false,
      width: '50px',
      align: 'center',
      renderType: 'status',
      renderConfig: {
        statusMap: {
          EM_DIA: { text: 'Em dia', class: 'status-available' },
          EM_ATRASO: { text: 'Em atraso', class: 'status-unavailable' },
          LIQUIDADO: { text: 'Liquidado', class: 'status-default' },
        },
      },
    },
  ];

  /**
   * Table configuration
   */
  public tableConfig: DataTableConfig = {
    columns: this.tableColumns,
    sortable: true,
    emptyMessage: 'Nenhum contrato encontrado',
    showActions: true,
    actionsColumnWidth: '50px',
    title: 'Lista de Empréstimos',
  };

  /**
   * Table actions configuration
   */
  public tableActions: DataTableAction[] = [
    {
      icon: 'cds-icon-search',
      label: '',
      action: (row: Record<string, unknown>) => this.openContractDetails(row),
      variant: 'solid',
      size: 'lg',
      showLabel: false,
    },
  ];

  /**
   * Component data including cards and contracts
   */
  public data: CreditLinesComponentData = {
    cards: {
      totalOutstandingBalance: 0,
      overdueContracts: 0,
      activeContracts: 0,
      balanceByInstitution: [],
      valueByCreditLine: [],
      valueByInstitution: [],
    },
    contracts: [],
  };

  /**
   * Change detector
   */
  private readonly cdr = inject(ChangeDetectorRef);

  /**
   * Credit lines service
   */
  private readonly creditLinesService = inject(CreditLinesService);

  /**
   * Modal service
   */
  private readonly modalService = inject(ModalService);

  /**
   * Open Finance directory service for dynamic bank logos
   */
  private readonly openFinanceDirectory = inject(OpenFinanceDirectoryService);

  /**
   * Tracking service
   */
  private readonly tracking = inject(TrackingService);

  /**
   * Map of tabs to productType
   */
  private readonly PRODUCT_TYPE_MAP: Record<string, string | undefined> = {
    emprestimos: 'EMPRESTIMOS',
    financiamentos: 'FINANCIAMENTOS',
    adiantamento: 'ADIANTAMENTO_A_DEPOSITANTES',
    'direitos-creditorios': 'DIREITOS_CREDITORIOS_DESCONTADOS',
  };

  /**
   * Map of table column keys to API sort keys
   */
  private readonly SORT_KEY_MAP: Record<string, string> = {
    productName: 'productName',
    isRevolvingCredit: 'isRevolvingCredit',
    contractAmount: 'contractAmount',
    outstandingBalance: 'outstandingBalance',
  };

  /**
   * Getter for table data based on active tab
   */
  public get tableData(): DataTableRow[] {
    return this.data.contracts.map((contract) => ({
      ...contract,
      institutionLogoUrl:
        getBankLogoUrl(contract.institutionCnpj, environment.staticUrl) ??
        this.openFinanceDirectory.getLogoUrl(contract.institutionCnpj) ??
        undefined,
    })) as unknown as DataTableRow[];
  }

  /**
   * Whether there's a next page to load
   */
  public get hasNextPage(): boolean {
    const pagination = this.pagination();
    return pagination.page < pagination.totalPages;
  }

  /**
   * Whether end-of-list indicator should be visible
   */
  public get showListEndIndicator(): boolean {
    return (
      !this.isLoadingContracts() &&
      !this.isLoadingMoreContracts() &&
      this.data.contracts.length > 0 &&
      !this.hasNextPage
    );
  }

  /**
   * Client identity number (CNPJ) derived from state
   */
  public get clientIdentityNumber(): string {
    return padValue(
      this.store.selectSnapshot(CompanyState.getActiveCompany)?.identity?.number || 0,
      14,
    );
  }

  /**
   * Initializing the component to update the displayedColumns array based on the current table configuration and available actions.
   */
  public override ngOnInit(): void {
    this.tracking.track(CAIXA_TRACKING_MODELS.creditLinesView);
    this.updateTableConfig(this.activeTab);
    this.store
      .select(CompanyState.getActiveCompany)
      .pipe(takeUntil(this.destroyController$))
      .subscribe(() => {
        this.loadSummary();
        this.loadContractsForActiveTab();
      });
    this.openFinanceDirectory.load();
    this.openFinanceDirectory.loaded$
      .pipe(takeUntil(this.destroyController$))
      .subscribe(() => this.cdr.markForCheck());
    super.ngOnInit();
  }

  /**
   * Get institution color for balance display
   *
   * @param institution - Institution name to get color for
   * @returns Color string for the institution
   */
  public getInstitutionColor(institution: string): string {
    const item = this.data.cards.balanceByInstitution.find((i) => i.institution === institution);
    return item?.color || 'var(--cds-text-light)';
  }

  /**
   * Get credit line color for display
   *
   * @param type - Credit line type to get color for
   * @returns Color string for the credit line type
   */
  public getCreditLineColor(type: string): string {
    const item = this.data.cards.valueByCreditLine.find((i) => i.type === type);
    return item?.color || 'var(--cds-text-light)';
  }

  /**
   * Get institution value color for display
   *
   * @param institution - Institution name to get color for
   * @returns Color string for the institution value
   */
  public getInstitutionValueColor(institution: string): string {
    const item = this.data.cards.valueByInstitution.find((i) => i.institution === institution);
    return item?.color || 'var(--cds-text-light)';
  }

  /**
   * Load summary data
   */
  private loadSummary(): void {
    this.isLoadingSummary.set(true);

    this.creditLinesService
      .getSummary(this.clientIdentityNumber)
      .pipe(take(1))
      .subscribe({
        next: (res) => {
          this.lastUpdate = new Date(res.lastUpdated);

          const { summary } = res;
          const financialInstitutions = summary.byFinancialInstitution || [];
          const byProductType = summary.byProductType || {};
          const palette = ['#8B5CF6', '#3B82F6', '#10B981', '#F59E0B', '#DC2626', '#1E40AF'];

          const balanceByInstitution: InstitutionBalance[] = financialInstitutions.map(
            (item, index) => ({
              institution: item.institutionName,
              balance: item.outstandingBalance || 0,
              color: palette[index % palette.length],
            }),
          );

          const valueByInstitution: InstitutionValue[] = financialInstitutions.map(
            (item, index) => ({
              institution: item.institutionName,
              value: item.contractedValue || 0,
              color: palette[index % palette.length],
            }),
          );

          const typeLabel: Record<string, { label: string; tooltip: string; color: string }> = {
            EMPRESTIMOS: {
              label: 'Empréstimos',
              tooltip:
                'Valores concedidos por instituições financeiras, com devolução prevista em prazo determinado e cobrança de juros.',
              color: '#8B5CF6',
            },
            FINANCIAMENTOS: {
              label: 'Financiamentos',
              tooltip:
                'Crédito destinado à compra de bens ou serviços, como veículos ou imóveis, com pagamento parcelado e cobrança de juros.',
              color: '#3B82F6',
            },
            ADIANTAMENTO_A_DEPOSITANTES: {
              label: 'Adiantamento a depositantes',
              tooltip:
                'Representam valores antecipados sobre receitas futuras e recursos mantidos em contas bancárias, como correntes ou poupança.',
              color: '#10B981',
            },
            DIREITOS_CREDITORIOS_DESCONTADOS: {
              label: 'Direitos creditórios descontados',
              tooltip:
                'Valores que uma empresa tem a receber no futuro, mas que são antecipados por uma instituição financeira mediante o pagamento de uma taxa.',
              color: '#DC2626',
            },
          };

          const valueByCreditLine: CreditLineValue[] = Object.keys(typeLabel).map((key) => {
            const meta = typeLabel[key];
            const productTypeData = byProductType[key];
            return {
              type: meta.label,
              value: productTypeData?.totalContractedValue ?? 0,
              color: meta.color,
              tooltip: meta.tooltip,
            };
          });

          this.data = {
            ...this.data,
            cards: {
              totalOutstandingBalance: summary.totalOutstandingBalance || 0,
              overdueContracts: summary.overdueContracts || 0,
              activeContracts: summary.activeContracts || 0,
              balanceByInstitution,
              valueByCreditLine,
              valueByInstitution,
            },
          };

          this.isLoadingSummary.set(false);
          this.cdr.markForCheck();
        },
        error: () => {
          this.isLoadingSummary.set(false);
          this.cdr.markForCheck();
        },
      });
  }

  /**
   * Load contracts for active tab
   */
  private loadContractsForActiveTab(): void {
    this.pagination.set({
      page: 1,
      pageSize: CreditLinesComponent.PAGE_SIZE,
      totalItems: 0,
      totalPages: 1,
    });

    this.data = { ...this.data, contracts: [] };
    this.tableConfig = { ...this.tableConfig };
    this.isLoadingContracts.set(true);
    this.cdr.markForCheck();
    this.loadContractsPage(1, false);
  }

  /**
   * Loads next contracts page when user reaches scroll threshold
   */
  public onInfiniteScroll(): void {
    if (this.isLoadingContracts() || this.isLoadingMoreContracts() || !this.hasNextPage) {
      return;
    }

    this.isLoadingMoreContracts.set(true);
    this.loadContractsPage(this.pagination().page + 1, true);
  }

  /**
   * Load contracts page for active tab
   *
   * @param page - Page number to load
   * @param append - Whether to append result to existing list
   */
  private loadContractsPage(page: number, append: boolean): void {
    const productType = this.PRODUCT_TYPE_MAP[this.activeTab];
    const apiSortBy = this.currentSort ? this.SORT_KEY_MAP[this.currentSort.sortBy] : undefined;
    const sortOrder = this.currentSort ? this.currentSort.sortOrder : undefined;

    this.creditLinesService
      .getCreditLines(
        this.clientIdentityNumber,
        productType,
        page,
        CreditLinesComponent.PAGE_SIZE,
        apiSortBy,
        sortOrder,
      )
      .pipe(take(1))
      .subscribe({
        next: (res: CreditLinesListApiResponse) => {
          const contracts = res.contracts || [];
          const currentContracts = append ? this.data.contracts : [];
          const pagination = res.pagination ?? {
            page,
            pageSize: CreditLinesComponent.PAGE_SIZE,
            totalItems: currentContracts.length + contracts.length,
            totalPages: 1,
          };

          this.data = { ...this.data, contracts: [...currentContracts, ...contracts] };
          this.pagination.set(pagination);
          this.isLoadingContracts.set(false);
          this.isLoadingMoreContracts.set(false);
          this.cdr.markForCheck();
        },
        error: () => {
          if (!append) {
            this.data = { ...this.data, contracts: [] };
          }
          this.isLoadingContracts.set(false);
          this.isLoadingMoreContracts.set(false);
          this.cdr.markForCheck();
        },
      });
  }

  /**
   * Handle tab change event
   *
   * @param tabId - ID of the tab to switch to
   * @returns void
   */
  public onTabChange(tabId: string): void {
    this.tracking.track(CAIXA_TRACKING_MODELS.creditLinesTabChange, { tab: tabId });
    this.activeTab = tabId;
    this.updateTableConfig(tabId);
    this.loadContractsForActiveTab();
  }

  /**
   * Open contract details modal
   *
   * @param contract - Contract data to show details for
   * @returns void
   */
  public openContractDetails(contract: Record<string, unknown>): void {
    const contractId = contract.contractId as string;

    if (!contractId || this.loadingDetails()) {
      return;
    }

    this.tracking.track(CAIXA_TRACKING_MODELS.creditLinesOpenDetails, {
      tab: this.activeTab,
      contractId,
    });

    const modalLoading = signal(true);
    const modalHasError = signal(false);
    const modalCreditLineData = signal<CreditLineDetailsData | null>(null);

    const modalRef: ModalRef = this.modalService.open(CreditLineDetailsModalContentComponent, {
      headerTitle: 'Detalhe da Linha de Crédito',
      creditLineData: modalCreditLineData,
      loading: modalLoading,
      hasError: modalHasError,
      size: 'lg',
    });

    this.loadingDetails.set(true);
    this.creditLinesService
      .getCreditLineDetails(contractId, this.clientIdentityNumber)
      .pipe(
        take(1),
        // FIX 86e1r18xt: closing the modal has to cancel the pending call, so that
        // finalize releases loadingDetails. Without it the guard at the top of this
        // method rejects every later click and details can never be opened again.
        takeUntil(modalRef?.afterClosed$ ?? NEVER),
        finalize(() => {
          modalLoading.set(false);
          this.loadingDetails.set(false);
        }),
      )
      .subscribe({
        next: (details: CreditLineDetailsResponse) => {
          if (!details?.contractId) {
            modalHasError.set(true);
            return;
          }

          const creditLineData: CreditLineDetailsData = this.mapApiResponseToModalData(details);
          modalCreditLineData.set(creditLineData);
        },
        error: () => {
          modalHasError.set(true);
        },
      });
  }

  /**
   * Map API response to modal data
   *
   * @param details - API response data to map
   * @returns Mapped modal data
   */
  private mapApiResponseToModalData(details: CreditLineDetailsResponse): CreditLineDetailsData {
    const hasInstallments =
      details.pastDueInstalments != 0 ||
      details.paidInstalments != 0 ||
      details.dueInstalments != 0;

    return {
      institution: details.brandName,
      productName: details.productName,
      productType: this.mapProductType(details.productType),
      contractAmount: this.formatCurrency(details.contractAmount),
      contractOutstandingBalance: withCashIndicator(
        this.formatCurrency(details.contractOutstandingBalance),
        details.contractOutstandingBalance,
        'debit',
      ),
      contractId: details.contractId,
      contractNumber: details?.contractNumber || 'N/A',
      contractDate: new Date(details.contractDate).toLocaleDateString('pt-BR'),
      dueDate: new Date(details.dueDate).toLocaleDateString('pt-BR'),
      paymentType: this.mapPaymentType(details),
      appliedRate: this.formatAppliedRate(details),
      contractStatus: this.mapPaymentStatus(details.paymentStatus),
      interestRateType: details.interestRateType || 'N/A',
      taxType: details.taxType || 'N/A',
      taxPeriodicity: this.mapTaxPeriodicity(details.taxPeriodicity),
      dueInstalments: details.dueInstalments?.toString(),
      paidInstalments: details.paidInstalments?.toString(),
      pastDueInstalments: details.pastDueInstalments?.toString(),
      hasInstallments,
      contractedInstallments: (
        details?.dueInstalments +
        details?.paidInstalments +
        details?.pastDueInstalments
      )?.toString(),
      installmentsToPay: details?.dueInstalments?.toString(),
      overdueInstallments: details.pastDueInstalments?.toString(),
      lastPaidInstallmentDate: details.lastPaidInstallmentDate
        ? new Date(details.lastPaidInstallmentDate).toLocaleDateString('pt-BR')
        : 'N/A',
      lastPaidInstallmentAmount: details.lastPaidInstallmentValue
        ? withCashIndicator(
            this.formatCurrency(details.lastPaidInstallmentValue),
            details.lastPaidInstallmentValue,
            'credit',
          )
        : 'N/A',
    };
  }

  /**
   * Map product type to display name
   *
   * @param productType - Product type to map
   * @returns Display name for the product type
   */
  private mapProductType(productType: string): string {
    const typeMap: Record<string, string> = {
      EMPRESTIMOS: 'Empréstimo',
      FINANCIAMENTOS: 'Financiamento',
      ADIANTAMENTO_A_DEPOSITANTES: 'Adiantamento a depositantes',
      DIREITOS_CREDITORIOS_DESCONTADOS: 'Direitos creditórios descontados',
    };
    return typeMap[productType] || productType;
  }

  /**
   * Map payment type
   *
   * @param details - Credit line details to determine payment type
   * @returns Display name for payment type
   */
  private mapPaymentType(details: CreditLineDetailsResponse): string {
    const isSingleInstallment =
      details.dueInstalments === 0 &&
      details.paidInstalments === 0 &&
      details.pastDueInstalments === 0;

    return isSingleInstallment ? 'Parcela única' : 'Parcelado';
  }

  /**
   * Map payment status
   *
   * @param status - Payment status to map
   * @returns Display name for payment status
   */
  private mapPaymentStatus(status: string): string {
    const statusMap: Record<string, string> = {
      EM_DIA: 'Em dia',
      EM_ATRASO: 'Em atraso',
      LIQUIDADO: 'Liquidado',
    };
    return statusMap[status] || 'Em dia';
  }

  /**
   * Map tax periodicity
   *
   * @param periodicity - Tax periodicity to map
   * @returns Display name for tax periodicity
   */
  private mapTaxPeriodicity(periodicity: string): string {
    const periodicityMap: Record<string, string> = {
      AA: 'ao ano',
      AM: 'ao mês',
    };
    return periodicityMap[periodicity] || periodicity;
  }

  /**
   * Format applied rate
   *
   * @param details - Credit line details to format rate from
   * @returns Formatted rate string
   */
  private formatAppliedRate(details: CreditLineDetailsResponse): string {
    const periodicity = this.mapTaxPeriodicity(details.taxPeriodicity);

    if (details.isPreFixedRate) {
      return `${details.preFixedRate ?? 0}% ${periodicity} pré-fixada`;
    }

    return `${details.indexerSubType} + ${details.posFixedRate}% ${periodicity}`;
  }

  /**
   * Format currency value to Brazilian Real with 2 decimal places
   *
   * @param value - Numeric value to format
   * @returns Formatted currency string
   */
  private formatCurrency(value: number): string {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  }

  /**
   * Handle table sort change event
   *
   * @param sort - Sort configuration
   * @returns void
   */
  public onSortChange(sort: DataTableSort): void {
    if (!this.SORT_KEY_MAP[sort.column]) {
      return;
    }

    let newSortOrder: 'asc' | 'desc' = 'asc';
    if (this.currentSort?.sortBy === sort.column && this.currentSort?.sortOrder === 'asc') {
      newSortOrder = 'desc';
    }

    this.currentSort = {
      sortBy: sort.column,
      sortOrder: newSortOrder,
    };

    this.tracking.track(CAIXA_TRACKING_MODELS.creditLinesSort, {
      tab: this.activeTab,
      sortBy: this.currentSort.sortBy,
      sortOrder: this.currentSort.sortOrder,
    });

    this.loadContractsForActiveTab();
  }

  /**
   * Update table configuration based on active tab
   *
   * @param tabId - ID of the tab to update configuration for
   * @returns void
   */
  private updateTableConfig(tabId: string): void {
    const tabConfigs = {
      emprestimos: {
        title: 'Lista de Empréstimos',
        emptyMessage: 'Nenhum empréstimo encontrado',
      },
      financiamentos: {
        title: 'Lista de Financiamentos',
        emptyMessage: 'Nenhum financiamento encontrado',
      },
      adiantamento: {
        title: 'Lista de Adiantamento a depositantes',
        emptyMessage: 'Nenhum "Adiantamento a depositante" encontrado.',
      },
      'direitos-creditorios': {
        title: 'Lista de Direitos creditórios descontados',
        emptyMessage: 'Nenhum "Direito creditório descontado" encontrado.',
      },
    };

    const config = tabConfigs[tabId as keyof typeof tabConfigs];
    if (config) {
      this.tableConfig = {
        ...this.tableConfig,
        title: config.title,
        emptyMessage: config.emptyMessage,
        currentSort: this.currentSort
          ? {
              column: this.currentSort.sortBy,
              direction: this.currentSort.sortOrder,
            }
          : undefined,
      };
    }
  }
}
